import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiService } from "../../services/axios";
import { toast } from "react-toastify";

export const fetchStudyMaterials = createAsyncThunk(
  "studyMaterial/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.get("/study-material/getAll");
      // API returns { status, data: [...] }
      if (Array.isArray(response?.data)) return response.data;
      if (Array.isArray(response)) return response;
      return [];
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const createStudyMaterial = createAsyncThunk(
  "studyMaterial/create",
  async (payload, { rejectWithValue }) => {
    try {
      // apiService.post returns full axios response
      const response = await apiService.post("/study-material/create", payload);
      const body = response?.data;
      toast.success(body?.message || "Study material created successfully");
      return body?.data || body;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const updateStudyMaterial = createAsyncThunk(
  "studyMaterial/update",
  async (payload, { rejectWithValue }) => {
    try {
      // apiService.put returns response.data already
      const body = await apiService.put("/study-material/update", payload);
      toast.success(body?.message || "Study material updated successfully");
      return body?.data || body;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const deleteStudyMaterial = createAsyncThunk(
  "studyMaterial/delete",
  async ({ studyMaterialId }, { rejectWithValue }) => {
    try {
      const response = await apiService.put("/study-material/delete", {
        studyMaterialId,
        isDelete: true,
      });
      toast.success(response?.message || "Study material deleted successfully");
      return studyMaterialId;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const fetchStudyMaterialFolders = createAsyncThunk(
  "studyMaterial/fetchFolders",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.get("/study-material/folder/getAll");
      if (Array.isArray(response?.data)) return response.data;
      if (Array.isArray(response)) return response;
      return [];
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const createStudyMaterialFolder = createAsyncThunk(
  "studyMaterial/createFolder",
  async (payload, { rejectWithValue }) => {
    try {
      // apiService.post returns full axios response
      const response = await apiService.post("/study-material/folder/create", payload);
      const body = response?.data;
      toast.success(body?.message || "Folder created successfully");
      return body?.data || body;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const updateStudyMaterialFolder = createAsyncThunk(
  "studyMaterial/updateFolder",
  async (payload, { rejectWithValue }) => {
    try {
      // apiService.put returns response.data already
      const body = await apiService.put("/study-material/folder/update", payload);
      toast.success(body?.message || "Folder updated successfully");
      return body?.data || body;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

export const deleteStudyMaterialFolder = createAsyncThunk(
  "studyMaterial/deleteFolder",
  async ({ folderId }, { rejectWithValue }) => {
    try {
      const response = await apiService.put("/study-material/folder/delete", {
        folderId,
        isDelete: true,
      });
      toast.success(response?.message || "Folder deleted successfully");
      return folderId;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  }
);

const studyMaterialSlice = createSlice({
  name: "studyMaterial",
  initialState: {
    materials: [],
    folders: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchStudyMaterials.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudyMaterials.fulfilled, (state, action) => {
        state.loading = false;
        state.materials = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchStudyMaterials.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch study materials";
      })
      .addCase(createStudyMaterial.fulfilled, (state, action) => {
        if (action.payload && action.payload._id) {
          state.materials.unshift(action.payload);
        }
      })
      .addCase(updateStudyMaterial.fulfilled, (state, action) => {
        const updated = action.payload;
        if (!updated?._id) return;
        const idx = state.materials.findIndex((m) => m._id === updated._id);
        if (idx !== -1) state.materials[idx] = updated;
      })
      .addCase(deleteStudyMaterial.fulfilled, (state, action) => {
        state.materials = state.materials.filter((m) => m._id !== action.payload);
      })
      .addCase(fetchStudyMaterialFolders.fulfilled, (state, action) => {
        state.folders = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(createStudyMaterialFolder.fulfilled, (state, action) => {
        if (action.payload && action.payload._id) {
          state.folders.push(action.payload);
        }
      })
      .addCase(updateStudyMaterialFolder.fulfilled, (state, action) => {
        const updated = action.payload;
        if (!updated?._id) return;
        const idx = state.folders.findIndex((f) => f._id === updated._id);
        if (idx !== -1) state.folders[idx] = updated;
      })
      .addCase(deleteStudyMaterialFolder.fulfilled, (state, action) => {
        state.folders = state.folders.filter((f) => f._id !== action.payload);
      });
  },
});

export default studyMaterialSlice.reducer;
